/**
 * @file 设备能力评估工具 (Device Capability Assessment)
 *
 * 功能说明：
 * 该工具用于评估浏览器环境下的设备能力，对标Netflix设备认证标准
 * 通过多维度测试（CPU、GPU、内存、解码、DRM、系统驱动、网络）计算设备等级
 *
 * 使用场景：
 * - 视频播放器根据设备能力选择合适的分辨率和码率
 * - 根据设备等级启用/禁用高级功能（如4K HDR）
 * - 低端设备自动优化UI和动画效果
 *
 */

// ==================== 常量配置 ====================

/**
 * 评分权重配置（对标Netflix体验影响优先级）
 * 权重总和应为1.0，各维度权重根据对用户体验的影响程度分配
 */
const SCORE_WEIGHTS = {
  cpu: 0.25,         // CPU性能权重 25% - 影响多任务稳定性和整体流畅度
  gpu: 0.3,          // GPU性能权重 30% - 核心指标，影响渲染流畅度和UI响应
  memory: 0.1,       // 内存权重 10% - 影响应用加载和后台运行能力
  decoding: 0.2,     // 解码能力权重 20% - 核心指标，决定可播放的视频质量
  drm: 0.05,         // DRM权重 5% - 权限门槛，L1是高端设备必要条件
  systemDriver: 0.05,// 系统驱动权重 5% - 影响硬件兼容性和稳定性
  networkStability: 0.05, // 网络稳定性权重 5% - 影响流媒体播放质量
};

/**
 * 设备等级阈值
 * 根据总分判定设备等级：high(高端) >= 80, mid(中端) >= 50, low(低端) < 50
 */
const LEVEL_THRESHOLDS = {
  high: 80,  // 高端设备阈值
  mid: 50,    // 中端设备阈值
};

/**
 * Netflix硬件认证基准值（用于量化打分）
 * 这些基准值基于Netflix对设备的实际测试和认证标准
 */
const NETFLIX_BENCHMARKS = {
  cpu: {
    low: 500,      // 低端CPU基准（DMIPS）
    mid: 1000,     // 中端CPU基准
    high: 1500,     // 高端CPU基准
  },
  gpu: {
    low: 20,       // 低端GPU基准（FPS）
    mid: 45,       // 中端GPU基准
    high: 60,       // 高端GPU基准
  },
  memory: {
    low: 1024,     // 低端内存基准（MB）
    mid: 2048,     // 中端内存基准
    high: 4096,     // 高端内存基准
  },
  network: {
    low: 3,        // 低端网络基准（Mbps）
    mid: 10,       // 中端网络基准
    high: 25,       // 高端网络基准
  },
};

// ==================== 工具函数 ====================

/**
 * 默认错误处理函数
 * 当检测过程中发生错误时，使用此函数记录错误信息
 *
 * @param {Object} error - 错误信息对象
 * @param {string} error.code - 错误代码
 * @param {string} error.message - 错误消息
 * @param {string} error.type - 错误类型
 */
const defaultErrorHandler = (error) => {
  console.warn(`[Device Assessment Error] ${error.type}: ${error.message}`, error);
};

// ==================== CPU性能测试 ====================

/**
 * CPU性能测试（对标Netflix DMIPS基准）
 *
 * 测试原理：
 * 通过计算质数来测试CPU的运算能力，这是Netflix使用的标准测试方法
 * 测试在Web Worker中执行，避免阻塞主线程
 *
 * 评分标准：
 * - 100分：执行时间 < 80ms（>1500 DMIPS，高端CPU）
 * - 75分：执行时间 < 150ms（1000-1500 DMIPS，中端CPU）
 * - 50分：执行时间 < 300ms（500-1000 DMIPS，低端CPU）
 * - 25分：执行时间 >= 300ms（<500 DMIPS，极低端CPU）
 *
 * @returns {Promise<number>} CPU性能得分（0-100）
 */
const testCPUPerformance = () => new Promise((resolve) => {
  try {
    // 检查是否支持Web Worker
    if (typeof Worker === 'undefined') {
      // 如果不支持Worker，使用同步方式（可能阻塞主线程）
      const start = performance.now();

      // 质数计算测试（Netflix基准测试用例）
      for (let i = 2; i < 15000; i += 1) {
        let isPrime = true;
        // 优化：只需检查到sqrt(i)
        for (let j = 2; j < Math.sqrt(i); j += 1) {
          if (i % j === 0) {
            isPrime = false;
            break;
          }
        }
        // 使用isPrime变量，避免未使用警告
        if (isPrime) {
          // 质数计数逻辑已完成
        }
      }

      const duration = performance.now() - start;
      // 映射Netflix DMIPS标准
      let score;
      if (duration < 80) {
        score = 100;
      } else if (duration < 150) {
        score = 75;
      } else if (duration < 300) {
        score = 50;
      } else {
        score = 25;
      }
      resolve(score);
      return;
    }

    // 使用Web Worker执行测试，避免阻塞主线程
    const workerCode = `
      self.onmessage = function(e) {
        const start = performance.now();

        // 质数计算测试
        for (let i = 2; i < 15000; i++) {
          let isPrime = true;
          for (let j = 2; j < Math.sqrt(i); j++) {
            if (i % j === 0) {
              isPrime = false;
              break;
            }
          }
          if (isPrime) {
            // 质数计数逻辑已完成
          }
        }

        const duration = performance.now() - start;
        // 映射Netflix DMIPS标准
        let score;
        if (duration < 80) {
          score = 100;
        } else if (duration < 150) {
          score = 75;
        } else if (duration < 300) {
          score = 50;
        } else {
          score = 25;
        }
        self.postMessage(score);
      };
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));

    worker.onmessage = (e) => {
      worker.terminate();
      URL.revokeObjectURL(blob);
      resolve(e.data);
    };

    worker.onerror = () => {
      worker.terminate();
      URL.revokeObjectURL(blob);
      resolve(50); // 默认中端分数
    };

    worker.postMessage('start');
  } catch (error) {
    resolve(50); // 默认中端分数
  }
});

// ==================== GPU性能测试 ====================

/**
 * 检测GPU基本信息
 *
 * 功能：
 * - 检测GPU渲染器名称和版本
 * - 检测WebGL支持情况
 * - 检测最大纹理尺寸
 *
 * @returns {Object} GPU信息对象
 */
const detectGPU = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');

    if (!gl) {
      return {
        renderer: 'unknown',
        version: 'unknown',
        maxTextureSize: 0,
        supportsWebGL2: false,
      };
    }

    return {
      renderer: gl.getParameter(gl.RENDERER) || 'unknown',
      version: gl.getParameter(gl.VERSION) || 'unknown',
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0,
      supportsWebGL2: !!canvas.getContext('webgl2'),
    };
  } catch (error) {
    return {
      renderer: 'unknown',
      version: 'unknown',
      maxTextureSize: 0,
      supportsWebGL2: false,
    };
  }
};

/**
 * GPU性能测试（对标Netflix 60fps标准）
 *
 * 测试原理：
 * 模拟Netflix UI场景：多图层合成（如海报列表滚动）
 * 通过测量实际渲染帧率来评估GPU性能
 *
 * 评分标准：
 * - 100分：FPS >= 60（高端GPU，流畅渲染）
 * - 75分：FPS >= 45（中端GPU，基本流畅）
 * - 50分：FPS >= 20（低端GPU，可接受）
 * - 25分：FPS < 20（极低端GPU，卡顿明显）
 *
 * @returns {Promise<number>} GPU性能得分（0-100）
 */
const testGPUPerformance = () => new Promise((resolve) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) {
      resolve(20); // 不支持WebGL，返回低分
      return;
    }

    let frameCount = 0;
    const duration = 1500; // 测试时长1.5秒
    const start = performance.now();

    /**
     * 渲染函数
     * 模拟绘制100个矩形（模拟海报列表场景）
     */
    const render = () => {
      if (performance.now() - start < duration) {
        // 清空画布
        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);

        // 绘制100个矩形模拟海报列表
        // 5列布局，每行20个
        for (let i = 0; i < 100; i += 1) {
          const x = (i % 5) * 100;
          const y = Math.floor(i / 5) * 150;
          gl.viewport(x, y, 80, 120);
          gl.clear(gl.COLOR_BUFFER_BIT);
        }

        frameCount += 1;
        requestAnimationFrame(render);
      } else {
        // 计算实际FPS
        const fps = Math.round(frameCount / (duration / 1000));

        // 映射Netflix FPS标准
        let score;
        if (fps >= 60) {
          score = 100;
        } else if (fps >= 45) {
          score = 75;
        } else if (fps >= 20) {
          score = 50;
        } else {
          score = 25;
        }
        resolve(score);
      }
    };

    render();
  } catch (error) {
    resolve(20); // 测试失败，返回低分
  }
});

// ==================== 内存评分 ====================

/**
 * 内存评分（对标Netflix内存门槛）
 *
 * 评分标准：
 * - 100分：jsHeapSizeLimit >= 4096MB（高端设备，≥4GB）
 * - 75分：jsHeapSizeLimit >= 2048MB（中端设备，≥2GB）
 * - 50分：jsHeapSizeLimit >= 1024MB（低端设备，≥1GB）
 * - 25分：jsHeapSizeLimit < 1024MB（极低端设备，<1GB）
 *
 * 注意：此评分基于JS堆内存限制，不是系统总内存
 *
 * @param {Function} onError - 错误回调函数
 * @returns {number} 内存得分（0-100）
 */
const estimateMemoryScore = (onError = defaultErrorHandler) => {
  try {
    if (performance && 'memory' in performance) {
      const { memory } = performance;
      // 转换为MB
      const totalJSHeap = memory.jsHeapSizeLimit / (1024 * 1024);

      let score;
      if (totalJSHeap >= 4096) {
        score = 100;
      } else if (totalJSHeap >= 2048) {
        score = 75;
      } else if (totalJSHeap >= 1024) {
        score = 50;
      } else {
        score = 25;
      }
      return score;
    }
    return 50; // 无法检测时默认中端
  } catch (error) {
    onError({
      code: 'MEMORY_SCORE_ERROR',
      message: 'Failed to estimate memory score',
      stack: error instanceof Error ? error.stack : undefined,
      type: 'ASSESSMENT',
    });
    return 50;
  }
};

// ==================== 视频解码能力测试 ====================

/**
 * 验证4K播放能力
 *
 * 功能：
 * 验证设备是否真正支持4K播放，避免虚假声明
 *
 * 注意：实际使用时需要真实的4K测试视频片段
 *
 * @returns {Promise<boolean>} 是否支持4K播放
 */
const verify4KPlayback = () => new Promise((resolve) => {
  try {
    const video = document.createElement('video');
    video.width = 3840;
    video.height = 2160;

    video.onloadedmetadata = () => {
      // 检查视频分辨率是否达到4K标准（3840x2160）
      resolve(video.videoWidth >= 3840 && video.videoHeight >= 2160);
    };

    video.onerror = () => resolve(false);

    // 由于无法直接测试，这里返回false
    // 实际使用时需要提供真实的4K测试视频
    setTimeout(() => resolve(false), 100);
  } catch {
    resolve(false);
  }
});

/**
 * 视频解码能力测试（对标Netflix编码优先级）
 *
 * 测试原理：
 * 检测浏览器支持的视频编码格式，按优先级从高到低：
 * 1. AV1 + 4K（最高质量，高端设备）
 * 2. HEVC + 1080P（高质量，中高端设备）
 * 3. H.264 + 1080P（标准质量，中端设备）
 * 4. H.264 + 720P（基础质量，低端设备）
 *
 * 评分标准：
 * - 100分：支持AV1 4K
 * - 75分：支持HEVC 1080P
 * - 50分：支持H.264 1080P
 * - 30分：仅支持H.264 720P
 *
 * @returns {Promise<Object>} 解码能力结果
 * @returns {Promise<number>} score - 解码得分（0-100）
 * @returns {Promise<string[]>} supportedFormats - 支持的格式列表
 */
const testVideoDecoding = async () => {
  // 测试格式列表，按优先级排序
  const testFormats = [
    {
      type: 'video/mp4;codecs="hvc1.1.6.L120.90"',
      name: 'HEVC-4K',
      score: 100,
    },
    {
      type: 'video/mp4; codecs="hev1.1.6.L93.B0"',
      name: 'HEVC-1080P',
      score: 75,
    },
    {
      type: 'video/mp4; codecs="avc1.4d4032"',
      name: 'H264-1080P',
      score: 50,
    },
    {
      type: 'video/mp4; codecs="avc1.4d401f"',
      name: 'H264-720P',
      score: 30,
    },
  ];

  let highestScore = 0;
  const supportedFormats = [];

  try {
    const video = document.createElement('video');

    for (const fmt of testFormats) {
      // 检测浏览器是否支持该格式
      const support = video.canPlayType(fmt.type);

      if (support === 'probably' || support === 'maybe') {
        if (fmt.score > highestScore) {
          highestScore = fmt.score;
        }
        supportedFormats.push(fmt.name);

        // 验证4K真实性（避免虚假声明）
        if (fmt.score === 100) {
          const canPlay4K = await verify4KPlayback();
          if (!canPlay4K) {
            highestScore = 75; // 降级到HEVC
          }
        }
      }
    }
  } catch (error) {
    // 如果检测失败，返回默认分数
    highestScore = highestScore || 30;
  }

  return {
    score: highestScore,
    supportedFormats: supportedFormats.length > 0 ? supportedFormats : undefined,
  };
};

// ==================== DRM检测 ====================

/**
 * DRM检测（L1作为高端必要条件）
 *
 * 功能说明：
 * DRM（数字版权管理）是流媒体服务的安全基础
 * - L1：硬件级安全，高端设备必需，支持最高质量内容
 * - L3：软件级安全，中端设备，支持标准质量内容
 * - none：不支持DRM，无法播放受保护内容
 *
 * Netflix规则：L1 DRM是高端设备的必要条件
 *
 * 评分标准：
 * - 100分：支持L1 DRM（高端设备）
 * - 50分：支持L3 DRM（中端设备）
 * - 0分：不支持DRM（低端设备或受限环境）
 *
 * @returns {Promise<Object>} DRM支持情况
 */
const testDRM = () => new Promise((resolve) => {
  try {
    // 检查是否支持MediaKeySystemAccess API
    if (!window.MediaKeySystemAccess) {
      return resolve({
        supported: false,
        securityLevel: 'none',
        score: 0,
        isHighEndEligible: false,
      });
    }

    // 优先检测L1（高端设备必需）
    navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
      initDataTypes: ['cenc'],
      videoCapabilities: [{
        contentType: 'video/mp4; codecs="avc1.4d401f"',
        robustness: 'SW_SECURE_CRYPTO',
      }],
    }])
      .then((ksa) => {
        try {
          // 获取配置信息
          const config = ksa.getConfiguration();
          const videoCapabilities = config.videoCapabilities || [];

          // 检查是否有L1级别的robustness
          const hasL1 = videoCapabilities.some(cap => cap.robustness === 'HW_SECURE_ALL' || cap.robustness === 'HW_SECURE_CRYPTO');

          if (hasL1) {
            resolve({
              supported: true,
              securityLevel: 'L1',
              score: 100,
              isHighEndEligible: true,
            });
          } else {
            resolve({
              supported: true,
              securityLevel: 'L3',
              score: 50,
              isHighEndEligible: false,
            });
          }
        } catch {
          // 如果无法获取配置，假设是L3
          resolve({
            supported: true,
            securityLevel: 'L3',
            score: 50,
            isHighEndEligible: false,
          });
        }
      })
      .catch(() => {
        // 降级检测L3
        navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
          initDataTypes: ['cenc'],
          videoCapabilities: [{
            contentType: 'video/mp4; codecs="avc1.4d401f"',
          }],
        }])
          .then(() => {
            resolve({
              supported: true,
              securityLevel: 'L3',
              score: 50,
              isHighEndEligible: false,
            });
          })
          .catch(() => {
            resolve({
              supported: false,
              securityLevel: 'none',
              score: 0,
              isHighEndEligible: false,
            });
          });
      });
  } catch (error) {
    resolve({
      supported: false,
      securityLevel: 'none',
      score: 0,
      isHighEndEligible: false,
    });
  }
});

// ==================== 系统驱动检测 ====================

/**
 * 系统驱动成熟度（对标Netflix兼容性要求）
 *
 * 功能说明：
 * 检测系统驱动的关键特性，这些特性影响硬件兼容性和性能
 *
 * 检测项：
 * - WebGL2：现代图形API支持
 * - DMA-BUF：零拷贝支持（Linux内核4.14+）
 * - Wayland：现代窗口系统
 * - GPU驱动：识别主流GPU驱动（Mali/Adreno/Intel/NVIDIA）
 *
 * 评分标准：
 * - 100分：支持全部4项特性
 * - 75分：支持3项特性
 * - 50分：支持2项特性
 * - 30分：支持1项特性
 * - 20分：不支持任何特性
 *
 * @returns {Object} 系统驱动信息
 */
const getSystemDriverInfo = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    const ua = navigator.userAgent;
    const isChromium = ua.includes('Chrome') || ua.includes('Chromium');
    const kernelVersionMatch = ua.match(/Linux (\d+\.\d+)/);
    const kernelVersion = kernelVersionMatch ? parseFloat(kernelVersionMatch[1]) : 0;

    // 关键驱动特性检测（Netflix硬件认证项）
    const supports = {
      webgl2: !!gl && gl.getParameter(gl.VERSION).includes('WebGL 2.0'),
      dmabuf: isChromium && kernelVersion >= 4.14, // 零拷贝支持
      wayland: ua.includes('Wayland'), // 现代窗口系统
      gpuDriver: gl ? (
        gl.getParameter(gl.RENDERER).includes('Mali')
        || gl.getParameter(gl.RENDERER).includes('Adreno')
        || gl.getParameter(gl.RENDERER).includes('Intel')
        || gl.getParameter(gl.RENDERER).includes('NVIDIA')
      ) : false,
    };

    // 计算支持的特性数量
    const featureCount = Object.values(supports).filter(v => v).length;

    // 根据支持的特性数量打分
    let score;
    if (featureCount === 4) {
      score = 100;
    } else if (featureCount === 3) {
      score = 75;
    } else if (featureCount === 2) {
      score = 50;
    } else if (featureCount === 1) {
      score = 30;
    } else {
      score = 20;
    }

    return { supports, score };
  } catch (error) {
    return {
      supports: {
        webgl2: false,
        dmabuf: false,
        wayland: false,
        gpuDriver: false,
      },
      score: 20,
    };
  }
};

// ==================== 网络稳定性测试 ====================

/**
 * 网络稳定性测试（Netflix网络基准）
 *
 * 测试原理：
 * 1. 通过fetch测试多个URL的响应时间，评估网络延迟
 * 2. 结合navigator.connection获取带宽信息
 * 3. 综合延迟和带宽计算网络得分
 *
 * 延迟评分标准：
 * - 100分：平均延迟 < 300ms（低延迟，高端网络）
 * - 75分：平均延迟 < 600ms（中延迟，中端网络）
 * - 40分：平均延迟 >= 600ms（高延迟，低端网络）
 *
 * 带宽评分标准：
 * - 100分：带宽 >= 25Mbps（高端，支持4K流媒体）
 * - 75分：带宽 >= 10Mbps（中端，支持1080P流媒体）
 * - 50分：带宽 >= 3Mbps（低端，支持720P流媒体）
 * - 20分：带宽 < 3Mbps（极低端，仅支持低质量）
 *
 * 最终得分 = 稳定性得分 * 60% + 带宽得分 * 40%
 *
 * @param {string[]} testUrls - 测试URL列表（可选）
 * @param {Function} onError - 错误回调函数（可选）
 * @returns {Promise<Object>} 网络稳定性结果
 */
const testNetworkStability = async (testUrls, onError = defaultErrorHandler) => {
  // 默认测试URL（小文件，用于测试延迟）
  const defaultUrls = [
    'https://www.google.com/favicon.ico',
    'https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png',
  ];

  const urls = testUrls || defaultUrls;
  let totalLatency = 0;
  let successCount = 0;

  // 测试每个URL的响应时间
  for (const url of urls) {
    try {
      const start = performance.now();
      await fetch(url, { mode: 'no-cors', cache: 'no-cache' });
      totalLatency += performance.now() - start;
      successCount += 1;
    } catch (e) {
      // 忽略单个URL的错误，继续测试其他URL
    }
  }

  // 计算平均延迟
  const avgLatency = successCount > 0 ? totalLatency / successCount : 0;

  // 根据延迟评分
  let stabilityScore;
  if (successCount === 0) {
    stabilityScore = 20;
  } else if (avgLatency < 300) {
    stabilityScore = 100;
  } else if (avgLatency < 600) {
    stabilityScore = 75;
  } else {
    stabilityScore = 40;
  }

  // 获取带宽信息
  let bandwidth = 1;
  try {
    if ('connection' in navigator) {
      const { connection } = navigator;
      bandwidth = connection.downlink || 1;
    }
  } catch (error) {
    if (onError) {
      onError({
        code: 'NETWORK_BANDWIDTH_ERROR',
        message: 'Failed to get network bandwidth',
        stack: error instanceof Error ? error.stack : undefined,
        type: 'NETWORK',
      });
    }
  }

  // 根据带宽评分
  let bandwidthScore;
  if (bandwidth >= 25) {
    bandwidthScore = 100;
  } else if (bandwidth >= 10) {
    bandwidthScore = 75;
  } else if (bandwidth >= 3) {
    bandwidthScore = 50;
  } else {
    bandwidthScore = 20;
  }

  // 网络总分 = 稳定性60% + 带宽40%
  const finalScore = Math.round((stabilityScore * 0.6) + (bandwidthScore * 0.4));

  return {
    avgLatency: successCount > 0 ? avgLatency : undefined,
    bandwidth,
    score: finalScore,
  };
};

// ==================== 用户体验测试 ====================

/**
 * 交互响应测试
 *
 * 功能：
 * 测试用户交互（如点击）的响应时间
 * 用于评估设备的整体响应性能
 *
 * @returns {Promise<number>} 响应时间（毫秒）
 */
const testInteractionResponse = () => new Promise((resolve) => {
  try {
    const btn = document.createElement('button');
    btn.style.position = 'fixed';
    btn.style.left = '-100px';
    btn.style.top = '-100px';
    document.body.appendChild(btn);

    const start = performance.now();
    btn.click();

    const handler = () => {
      const time = performance.now() - start;
      if (document.body.contains(btn)) {
        document.body.removeChild(btn);
      }
      resolve(time);
    };

    btn.addEventListener('click', handler, { once: true });

    // 超时保护（1秒）
    setTimeout(() => {
      if (document.body.contains(btn)) {
        document.body.removeChild(btn);
        resolve(100); // 默认响应时间
      }
    }, 1000);
  } catch {
    resolve(100);
  }
});

/**
 * 获取加载性能
 *
 * 功能：
 * 从Performance API获取页面加载性能指标
 *
 * @returns {Object} 加载性能数据
 */
const getLoadPerformance = () => {
  try {
    const navEntry = performance.getEntriesByType('navigation')[0];
    if (navEntry) {
      return {
        domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.startTime,
        loadEvent: navEntry.loadEventEnd - navEntry.startTime,
      };
    }
  } catch {
    // 忽略错误
  }
  return { domContentLoaded: 0, loadEvent: 0 };
};

// ==================== 评估主函数 ====================

/**
 * 检测硬件性能
 *
 * 功能：
 * 综合测试CPU、GPU、内存、解码能力
 *
 * @param {Function} onError - 错误回调函数
 * @returns {Promise<Object>} 硬件评估结果
 */
const detectHardware = async (onError = defaultErrorHandler) => {
  const cpuCores = navigator.hardwareConcurrency || 2;
  const cpuScore = await testCPUPerformance();
  const gpuInfo = detectGPU();
  const gpuScore = await testGPUPerformance();
  const memoryScore = estimateMemoryScore(onError);
  const decodingResult = await testVideoDecoding();

  return {
    cpu: {
      cores: cpuCores,
      score: cpuScore,
    },
    gpu: {
      ...gpuInfo,
      score: gpuScore,
    },
    memory: {
      score: memoryScore,
    },
    decoding: {
      score: decodingResult.score,
      supportedFormats: decodingResult.supportedFormats,
    },
  };
};

/**
 * 检测系统能力
 *
 * 功能：
 * 综合测试DRM、系统驱动、网络稳定性
 *
 * @param {string[]} testUrls - 网络测试URL列表（可选）
 * @param {Function} onError - 错误回调函数（可选）
 * @returns {Promise<Object>} 系统评估结果
 */
const detectSystem = async (testUrls, onError = defaultErrorHandler) => {
  const drmSupport = await testDRM();
  const systemInfo = getSystemDriverInfo();
  const networkInfo = await testNetworkStability(testUrls, onError);

  return {
    drm: drmSupport,
    systemDriver: systemInfo,
    networkStability: networkInfo,
  };
};

/**
 * 测量用户体验
 *
 * 功能：
 * 测试交互响应时间和页面加载性能
 *
 * @returns {Promise<Object>} 用户体验评估结果
 */
const measureExperience = async () => {
  const responseTime = await testInteractionResponse();
  const loadPerformance = getLoadPerformance();

  return {
    responseTime,
    loadPerformance,
  };
};

/**
 * 计算总分与设备等级（加入Netflix一票否决逻辑）
 *
 * 计算逻辑：
 * 1. 根据权重计算加权总分
 * 2. 应用Netflix核心规则：L1 DRM是高端设备必要条件
 *
 * 等级判定规则：
 * - high（高端）：总分 >= 80 且 支持L1 DRM
 * - mid（中端）：总分 >= 50
 * - low（低端）：总分 < 50
 *
 * @param {Object} hardware - 硬件评估结果
 * @param {Object} system - 系统评估结果
 * @returns {Object} 设备等级信息
 */
const calculateLevel = (hardware, system) => {
  // 加权计算总分
  const totalScore = Math.round(hardware.cpu.score * SCORE_WEIGHTS.cpu
    + hardware.gpu.score * SCORE_WEIGHTS.gpu
    + hardware.memory.score * SCORE_WEIGHTS.memory
    + hardware.decoding.score * SCORE_WEIGHTS.decoding
    + system.drm.score * SCORE_WEIGHTS.drm
    + system.systemDriver.score * SCORE_WEIGHTS.systemDriver
    + system.networkStability.score * SCORE_WEIGHTS.networkStability);

  // Netflix核心规则：L1 DRM是高端设备必要条件
  let level;
  if (totalScore >= LEVEL_THRESHOLDS.high && system.drm.isHighEndEligible) {
    level = 'high';
  } else if (totalScore >= LEVEL_THRESHOLDS.mid) {
    level = 'mid';
  } else {
    level = 'low';
  }

  return { totalScore, level };
};

// ==================== 导出主函数 ====================

/**
 * 执行完整设备能力评估
 *
 * 功能说明：
 * 这是主要的导出函数，执行完整的设备能力评估流程
 *
 * 评估流程：
 * 1. 检测硬件性能（CPU、GPU、内存、解码）
 * 2. 检测系统能力（DRM、驱动、网络）
 * 3. 测量用户体验（响应时间、加载性能）
 * 4. 计算总分和设备等级
 *
 * 使用示例：
 *vascript
 * const assessment = await assessDeviceCapability({
 *   onError: (error) => {
 *     console.error('评估错误:', error);
 *   },
 *   testNetworkUrls: [
 *     'https://example.com/test1',
 *     'https://example.com/test2'
 *   ]
 * });
 *
 * if (assessment) {
 *   console.log('设备等级:', assessment.level.level); // 'high' | 'mid' | 'low'
 *   console.log('总分:', assessment.level.totalScore);
 *   console.log('CPU得分:', assessment.hardware.cpu.score);
 *   console.log('GPU得分:', assessment.hardware.gpu.score);
 * }
 *  *
 * 返回结果结构：
 *t
 * {
 *   hardware: {
 *     cpu: { cores: 4, score: 75 },
 *     gpu: { renderer: '...', score: 80 },
 *     memory: { score: 75 },
 *     decoding: { score: 100, supportedFormats: ['AV1-4K'] }
 *   },
 *   system: {
 *     drm: { supported: true, securityLevel: 'L1', score: 100, isHighEndEligible: true },
 *     systemDriver: { supports: {...}, score: 75 },
 *     networkStability: { avgLatency: 200, bandwidth: 25, score: 90 }
 *   },
 *   experience: {
 *     responseTime: 10,
 *     loadPerformance: { domContentLoaded: 500, loadEvent: 1000 }
 *   },
 *   level: {
 *     totalScore: 85,
 *     level: 'high'
 *   }
 * }
 *  *
 * @param {Object} options - 配置选项
 * @param {Function} options.onError - 错误回调函数（可选）
 * @param {string[]} options.testNetworkUrls - 网络测试URL列表（可选）
 * @returns {Promise<Object|null>} 设备评估结果，失败时返回null
 */
const assessDeviceCapability = async (options = {}) => {
  const {
    onError = defaultErrorHandler,
    testNetworkUrls,
  } = options;

  try {
    // 1. 检测硬件性能
    const hardware = await detectHardware(onError);

    // 2. 检测系统能力
    const system = await detectSystem(testNetworkUrls, onError);

    // 3. 测量用户体验
    const experience = await measureExperience();

    // 4. 计算总分和设备等级
    const level = calculateLevel(hardware, system);

    return {
      level,
      experience,
      hardware,
      system,
    };
  } catch (error) {
    onError({
      code: 'ASSESSMENT_ERROR',
      message: 'Failed to assess device capability',
      stack: error instanceof Error ? error.stack : undefined,
      type: 'ASSESSMENT',
    });
    return null;
  }
};

// ==================== 导出 ====================

export {
  assessDeviceCapability,
  testCPUPerformance,
  testGPUPerformance,
  testVideoDecoding,
  testDRM,
  testNetworkStability,
  SCORE_WEIGHTS,
  LEVEL_THRESHOLDS,
  NETFLIX_BENCHMARKS,
};
